import React from 'react';
import {Img, staticFile} from 'remotion';
import {C, F, clamp01} from './util';

type Sized = {w: number; h: number; faces?: number[][]};
// Where a photo shown with `object-fit: cover` in a W×H frame lands, framed on its faces: the crop is
// shifted so the union of the detected faces sits as close to the frame centre as the image allows.
// Photos without detected faces are framed on the upper third, where people's heads usually are.
export const fit = (p: Sized, W: number, H: number) => {
 const s = Math.max(W / p.w, H / p.h);
 const dw = p.w * s, dh = p.h * s;
 const fs = p.faces ?? [];
 const cx = fs.length ? (Math.min(...fs.map(f => f[0])) + Math.max(...fs.map(f => f[0] + f[2]))) / 2 : 0.5;
 const cy = fs.length ? (Math.min(...fs.map(f => f[1])) + Math.max(...fs.map(f => f[1] + f[3]))) / 2 : 0.33;
 const ox = Math.min(0, Math.max(W - dw, W / 2 - cx * dw));
 const oy = Math.min(0, Math.max(H - dh, H / 2 - cy * dh));
 return {dw, dh, ox, oy};
};

// Maps a face box (fraction of the original photo) onto the photo as framed by `fit`.
export const coverBox = (p: Sized, f: number[], W: number, H: number) => {
 const {dw, dh, ox, oy} = fit(p, W, H);
 return {x: ox + f[0] * dw, y: oy + f[1] * dh, w: f[2] * dw, h: f[3] * dh};
};

// Framed photo. The frame is the BACKGROUND of the outer element and the image lives in an inner block
// (border + background on one element leaves a jagged seam). `dim` darkens on top without touching opacity.
// Pass `photo` (with its faces) and the crop frames the faces; `shift` nudges the crop, in px, for bursts.
export const PhotoTile: React.FC<{src: string; photo?: Sized; w: number; h: number; r?: number; dim?: number; ring?: number; shift?: [number, number]; zoom?: number; style?: React.CSSProperties; children?: React.ReactNode}> =
 ({src, photo, w, h, r = 12, dim = 0, ring = 0, shift = [0, 0], zoom = 1, style, children}) => {
  const inset = ring > 0 ? 2 : 1;
  const iw = w - 2 * inset, ih = h - 2 * inset;
  const g = fit(photo ?? {w: iw, h: ih}, iw, ih);
  return <div style={{position: 'absolute', width: w, height: h, borderRadius: r, background: ring > 0 ? `rgba(113,112,255,${ring})` : C.lineHi,
   boxShadow: '0 18px 40px rgba(0,0,0,.45)', ...style}}>
   <div style={{position: 'absolute', inset, borderRadius: r - 1, overflow: 'hidden', background: C.card}}>
    {photo
     ? <Img src={staticFile(src)} style={{position: 'absolute', left: g.ox + shift[0], top: g.oy + shift[1], width: g.dw, height: g.dh, maxWidth: 'none',
        transform: zoom !== 1 ? `scale(${zoom})` : undefined}}/>
     : <Img src={staticFile(src)} style={{width: '100%', height: '100%', objectFit: 'cover', objectPosition: '50% 33%', display: 'block'}}/>}
    {dim > 0 && <div style={{position: 'absolute', inset: 0, background: `rgba(8,9,10,${dim})`}}/>}
    {children}
   </div>
  </div>;
 };

// Face box that draws itself (a stroke running around the rectangle), in frame pixels (see coverBox).
export const FaceBox: React.FC<{x: number; y: number; w: number; h: number; k: number; label?: string; color?: string; labelK?: number}> =
 ({x, y, w, h, k, label, color = C.accent, labelK}) => {
  if (k <= 0) return null;
  const per = 2 * (w + h);
  const lk = labelK ?? clamp01((k - 0.6) / 0.4);
  return <>
   <svg style={{position: 'absolute', left: x - 2, top: y - 2, overflow: 'visible'}} width={w + 4} height={h + 4}>
    <rect x={2} y={2} width={w} height={h} rx={Math.min(8, w / 8)} fill="none" stroke={color} strokeWidth={2.5}
     strokeDasharray={per} strokeDashoffset={per * (1 - k)} style={{filter: `drop-shadow(0 0 6px ${color})`}}/>
   </svg>
   {label && lk > 0 && <div style={{position: 'absolute', left: x, top: y + h + 6, transform: `translateY(${(1 - lk) * 6}px)`, opacity: lk,
    background: color, color: '#fff', fontFamily: F.sans, fontWeight: 600, fontSize: 15, padding: '4px 9px', borderRadius: 6, whiteSpace: 'nowrap'}}>{label}</div>}
  </>;
 };

// Round avatar cropped on the reference face.
export const Avatar: React.FC<{src: string; face: number[]; iw: number; ih: number; size: number; ring?: boolean}> = ({src, face, iw, ih, size, ring}) => {
 const fw = face[2] * iw, s = (size * 0.5) / fw;
 const cx = (face[0] + face[2] / 2) * iw * s, cy = (face[1] + face[3] / 2) * ih * s;
 return <div style={{width: size, height: size, borderRadius: '50%', background: ring ? C.accent : C.lineHi, padding: ring ? 3 : 1, boxSizing: 'border-box', flex: 'none'}}>
  <div style={{width: '100%', height: '100%', borderRadius: '50%', overflow: 'hidden', position: 'relative'}}>
   <Img src={staticFile(src)} style={{position: 'absolute', width: iw * s, height: ih * s, left: size / 2 - cx, top: size / 2 - cy, maxWidth: 'none'}}/>
  </div>
 </div>;
};

export const Card: React.FC<{w: number; h: number; style?: React.CSSProperties; children?: React.ReactNode; r?: number}> = ({w, h, style, children, r = 16}) => (
 <div style={{position: 'absolute', width: w, height: h, borderRadius: r, background: C.lineHi, boxShadow: '0 24px 60px rgba(0,0,0,.5)', ...style}}>
  <div style={{position: 'absolute', inset: 1, borderRadius: r - 1, background: C.card, overflow: 'hidden'}}>{children}</div>
 </div>
);

export const Mono: React.FC<{children: React.ReactNode; color?: string; size?: number; style?: React.CSSProperties}> = ({children, color = C.muted, size = 14, style}) => (
 <div style={{fontFamily: F.mono, fontSize: size, letterSpacing: '.14em', textTransform: 'uppercase', color, fontWeight: 500, ...style}}>{children}</div>
);

// Stroke icons (24×24), same weight as the Lucide icons the product uses.
const paths: Record<string, string> = {
 eyeOff: 'M3 3l18 18M10.6 10.6a2 2 0 0 0 2.8 2.8M9.9 5.1A10.4 10.4 0 0 1 12 5c5 0 9 4.5 10 7-.4 1-1.2 2.3-2.4 3.5M6.6 6.6C4.4 8 2.8 10.2 2 12c1 2.5 5 7 10 7 1.8 0 3.4-.5 4.8-1.3',
 unlink: 'M9 17H7A5 5 0 0 1 7 7h2M15 7h2a5 5 0 0 1 4 8M8 12h3M3 3l18 18',
 swap: 'M7 4v16M3 8l4-4 4 4M17 20V4M13 16l4 4 4-4',
 mail: 'M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1zM3 7l9 6 9-6',
 send: 'M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z',
 eye: 'M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
 open: 'M4 20V9l8-5 8 5v11M4 9l8 5 8-5',
 download: 'M12 3v12M7 10l5 5 5-5M4 19h16',
 check: 'M5 12l5 5 9-10',
 clock: 'M12 7v5l3 2M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z',
 lock: 'M6 11h12v9H6zM8 11V7a4 4 0 0 1 8 0v4',
 unlock: 'M6 11h12v9H6zM8 11V7a4 4 0 0 1 7.5-2',
 star: 'M12 3l2.8 5.8 6.2.9-4.5 4.4 1 6.3L12 17.5 6.5 20.4l1-6.3L3 9.7l6.2-.9z',
 layers: 'M12 3l9 5-9 5-9-5 9-5zM3 13l9 5 9-5',
 image: 'M4 4h16v16H4zM4 16l5-5 4 4 3-3 4 4M15 9a1 1 0 1 0 0-.01',
 folder: 'M3 6a1 1 0 0 1 1-1h5l2 2h9a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z',
 user: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21a8 8 0 0 1 16 0',
 users: 'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM2 21a7 7 0 0 1 14 0M16 3.1a4 4 0 0 1 0 7.8M22 21a7 7 0 0 0-4-6.3',
 coins: 'M9 13a6 6 0 1 0 0-12 6 6 0 0 0 0 12zM18.1 10.4A6 6 0 1 1 10.3 18M7 5h2v4',
 zap: 'M13 2L3 14h9l-1 8 10-12h-9l1-8z',
 database: 'M12 8c4.4 0 8-1.3 8-3s-3.6-3-8-3-8 1.3-8 3 3.6 3 8 3zM4 5v14c0 1.7 3.6 3 8 3s8-1.3 8-3V5M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3',
 refresh: 'M21 12a9 9 0 0 1-15.5 6.2L3 16M3 12A9 9 0 0 1 18.5 5.8L21 8M21 3v5h-5M3 21v-5h5',
 calendar: 'M4 5h16v16H4zM4 10h16M8 3v4M16 3v4M12 13v5M9.5 15.5h5',
 scan: 'M3 7V4h3M18 4h3v3M21 17v3h-3M6 20H3v-3M8 12h8',
 file: 'M14 3H6v18h12V7zM14 3v4h4M9 13h6M9 17h6',
 arrowUp: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 16V8M8 12l4-4 4 4',
 arrowDown: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 8v8M8 12l4 4 4-4',
 archive: 'M3 4h18v4H3zM5 8v12h14V8M10 12h4',
 card: 'M3 6h18v12H3zM3 10h18M7 15h4',
 pix: 'M12 3l9 9-9 9-9-9zM8 12l4-4 4 4-4 4z',
 history: 'M3 12a9 9 0 1 0 3-6.7L3 8M3 3v5h5M12 7v5l3 2',
 copy: 'M8 8h12v12H8zM16 8V4H4v12h4',
 cloudOff: 'M3 3l18 18M7 18h10a4 4 0 0 0 1.5-7.7A6 6 0 0 0 8.2 7.4M5.3 9.3A4.5 4.5 0 0 0 7 18',
 x: 'M6 6l12 12M18 6L6 18',
 sheet: 'M4 3h16v18H4zM4 9h16M4 15h16M10 3v18',
 hdd: 'M3 13h18v6H3zM5 13l2-8h10l2 8M7 16h.01M11 16h6',
 globe: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM3 12h18M12 3c2.5 3 2.5 15 0 18M12 3c-2.5 3-2.5 15 0 18',
 usb: 'M12 2v14M8 6l4-4 4 4M7 12h2v4H7zM15 10h2v3h-2zM8 16c0 2 2 3 4 3s4-1 4-3M12 19v3',
 monitor: 'M3 4h18v12H3zM8 20h8M12 16v4',
 shield: 'M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z',
 type: 'M4 7V5h16v2M9 19h6M12 5v14',
 split: 'M3 6h7l4 6-4 6H3M14 12h7M17 9l3 3-3 3',
 search: 'M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14zM21 21l-5-5',
 alert: 'M12 3l10 18H2zM12 10v5M12 18h.01',
 grid: 'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z',
 plus: 'M12 5v14M5 12h14',
 bars: 'M5 20V11M11 20V5M17 20v-7M3 20h18',
 help: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM9.6 9.2a2.5 2.5 0 0 1 4.8.9c0 1.6-2.4 2.1-2.4 3.6M12 17h.01',
 chevrons: 'M8 9l4-4 4 4M8 15l4 4 4-4',
 back: 'M19 12H5M11 6l-6 6 6 6',
};
export const Icon: React.FC<{name: keyof typeof paths | string; size?: number; color?: string; stroke?: number}> = ({name, size = 24, color = C.ink, stroke = 1.8}) => (
 <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" style={{flex: 'none', display: 'block'}}>
  <path d={paths[name]}/>
 </svg>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
